import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Search, Check, Clock, ArrowRight, Globe, Lock, LogOut } from 'lucide-react';
import { getMyOrgs, createOrg, discoverOrgs, requestToJoin, getAppConfig, leaveOrg } from '../api';
import { getActiveOrg, setActiveOrg, clearActiveOrg } from '../org';

export default function Organizations() {
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  // Platform off-switch: when false, self-serve org creation is disabled.
  const [canCreate, setCanCreate] = useState(true);

  // create form
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [joinPolicy, setJoinPolicy] = useState('approval');

  // discover
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [discoverTotal, setDiscoverTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const INITIAL_SHOW = 5;

  const flash = (text, type = 'success') => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const loadMine = useCallback(async () => {
    setLoading(true);
    try { const r = await getMyOrgs(); setMine(r.data.orgs || []); }
    catch { /* */ } finally { setLoading(false); }
  }, []);

  const loadDiscover = useCallback(async (query) => {
    try {
      const r = await discoverOrgs(query);
      setResults(r.data.orgs || []);
      setDiscoverTotal(r.data.total || r.data.orgs?.length || 0);
      // reset "show all" when search query changes
      setShowAll(false);
    } catch { /* */ }
  }, []);

  useEffect(() => { loadMine(); loadDiscover(''); }, [loadMine, loadDiscover]);
  useEffect(() => {
    getAppConfig()
      .then((r) => { const allow = r.data?.allow_org_creation !== false; setCanCreate(allow); if (!allow) setShowCreate(false); })
      .catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(() => loadDiscover(q.trim()), 250); return () => clearTimeout(t); }, [q, loadDiscover]);

  const enter = (o) => { setActiveOrg(o.org_id, o.name, o.role, o.permissions); window.location.assign('/dashboard'); };

  const leave = async (o) => {
    if (!window.confirm(`Leave ${o.name}? You'll lose access until you're invited back.`)) return;
    try {
      await leaveOrg(o.org_id);
      if (o.org_id === getActiveOrg()) clearActiveOrg();
      flash(`You left ${o.name}`);
      await loadMine();
    } catch (err) { flash(err.response?.data?.detail || 'Could not leave', 'error'); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await createOrg(name.trim(), visibility, visibility === 'public' ? joinPolicy : 'approval');
      setActiveOrg(r.data.org_id, r.data.name, r.data.role, r.data.permissions);
      flash('Organization created');
      window.location.assign('/dashboard');
    } catch (err) { flash(err.response?.data?.detail || 'Could not create', 'error'); }
  };

  const join = async (o) => {
    try {
      const r = await requestToJoin(o.org_id);
      if (r.data.joined) { setActiveOrg(o.org_id, o.name, r.data.role, r.data.permissions); flash(`Joined ${o.name}`); window.location.assign('/dashboard'); }
      else { flash('Request sent — an admin will review it'); loadDiscover(q.trim()); }
    } catch (err) { flash(err.response?.data?.detail || 'Could not request', 'error'); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">Organizations</h1>
          <p className="text-fg-muted text-sm mt-1">{canCreate ? 'Create an organization, or find and join a public one.' : 'Find and join a public organization, or ask an admin for an invite.'}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate((v) => !v)} className="flex items-center gap-2 px-4 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm">
            <Plus className="w-4 h-4" /> New organization
          </button>
        )}
      </div>

      {!canCreate && (
        <div className="mb-4 p-3 rounded-lg text-sm bg-field border border-panel-border text-fg-muted flex items-center gap-2">
          <Lock className="w-4 h-4 shrink-0" />
          Organization creation is disabled by your administrator. Join a public organization below, or ask an admin to invite you.
        </div>
      )}

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>{msg.text}</div>
      )}

      {/* create form */}
      {canCreate && showCreate && (
        <form onSubmit={create} className="mb-6 bg-panel border border-panel-border rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-xs text-fg-muted mb-1">Organization name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Acme Inc."
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange" />
          </div>
          <div className="flex flex-wrap gap-3">
            {['private', 'public'].map((v) => (
              <button type="button" key={v} onClick={() => setVisibility(v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${visibility === v ? 'border-orange bg-orange/10 text-fg' : 'border-panel-border text-fg-muted'}`}>
                {v === 'public' ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                {v === 'public' ? 'Public — discoverable' : 'Private — invite only'}
              </button>
            ))}
          </div>
          {visibility === 'public' && (
            <div>
              <label className="block text-xs text-fg-muted mb-1">When someone requests to join</label>
              <div className="flex gap-3">
                {[['approval', 'Admin approves'], ['auto', 'Auto-approve (join instantly)']].map(([v, label]) => (
                  <button type="button" key={v} onClick={() => setJoinPolicy(v)}
                    className={`px-3 py-2 rounded-lg border text-sm ${joinPolicy === v ? 'border-orange bg-orange/10 text-fg' : 'border-panel-border text-fg-muted'}`}>{label}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm">Create</button>
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 text-fg-muted hover:text-fg text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* my orgs */}
      <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-widest mb-2">Your organizations</h2>
      {loading ? <p className="text-fg-muted text-sm">Loading…</p> : mine.length === 0 ? (
        <p className="text-fg-muted text-sm mb-6">{canCreate ? "You're not in any organization yet. Create one, or find a public one below." : "You're not in any organization yet. Find a public one below, or ask an admin for an invite."}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {mine.map((o) => (
            <div key={o.org_id}
              className="bg-panel border border-panel-border rounded-xl p-4 hover:border-orange/40 flex items-center justify-between gap-3 group">
              <button onClick={() => enter(o)} className="text-left flex items-center gap-3 min-w-0 flex-1">
                <div className="p-2 rounded-lg bg-orange/10"><Building2 className="w-5 h-5 text-orange" /></div>
                <div className="min-w-0">
                  <div className="text-fg font-semibold truncate">{o.name}</div>
                  <div className="text-fg-muted text-xs">{o.role}{o.org_id === getActiveOrg() ? ' · active' : ''}</div>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                {/* An owner has to hand the org over before they can leave it. */}
                {o.role !== 'owner' && (
                  <button onClick={() => leave(o)} title="Leave this organization"
                    className="p-1.5 rounded-lg text-fg-muted hover:text-red-400 hover:bg-fg/5">
                    <LogOut className="w-4 h-4" />
                  </button>
                )}
                <ArrowRight className="w-4 h-4 text-fg-muted group-hover:text-fg" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* discover */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-widest">Discover public organizations</h2>
        {discoverTotal > 0 && (
          <span className="text-xs text-fg-muted bg-fg/5 border border-panel-border px-2.5 py-1 rounded-full font-semibold">
            {discoverTotal} org{discoverTotal !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-fg-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search public organizations…"
          className="w-full pl-9 pr-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange" />
      </div>
      <div className="space-y-2">
        {(showAll || q.trim() ? results : results.slice(0, INITIAL_SHOW)).map((o) => (
          <div key={o.org_id} className="flex items-center justify-between bg-panel border border-panel-border rounded-xl p-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-orange/10"><Building2 className="w-5 h-5 text-orange" /></div>
              <div className="min-w-0">
                <div className="text-fg font-semibold truncate">{o.name}</div>
                <div className="text-fg-muted text-xs">{o.member_count} member{o.member_count !== 1 ? 's' : ''} · {o.join_policy === 'auto' ? 'joins instantly' : 'admin approval'}</div>
              </div>
            </div>
            {o.my_status === 'member' ? (
              <span className="flex items-center gap-1 text-xs text-green-400"><Check className="w-4 h-4" /> Member</span>
            ) : o.my_status === 'pending' ? (
              <span className="flex items-center gap-1 text-xs text-fg-muted"><Clock className="w-4 h-4" /> Requested</span>
            ) : (
              <button onClick={() => join(o)} className="px-3 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-xs">
                {o.join_policy === 'auto' ? 'Join' : 'Request to join'}
              </button>
            )}
          </div>
        ))}

        {/* Show more / less — only when no search query active */}
        {!q.trim() && results.length > INITIAL_SHOW && (
          <button
            onClick={() => setShowAll(v => !v)}
            className="w-full mt-1 py-2 text-sm text-fg-muted hover:text-fg border border-panel-border rounded-xl
              bg-panel hover:bg-fg/5 transition-colors flex items-center justify-center gap-2"
          >
            {showAll
              ? `Show less`
              : `Show ${results.length - INITIAL_SHOW} more organization${results.length - INITIAL_SHOW !== 1 ? 's' : ''}`}
          </button>
        )}

        {results.length === 0 && (
          <p className="text-fg-muted text-sm py-2">
            {q.trim() ? `No public organizations matching "${q}".` : 'No public organizations found.'}
          </p>
        )}
      </div>
    </div>
  );
}
